import {AVATAR_COLORS, avatarSrc, initials} from '../../../utils/avatar';
import React, {FC} from 'react';
import {Avatar, AvatarGroup, Card, CardActionArea, Stack, Typography} from "@mui/material";
import {GroupsRounded} from "@mui/icons-material";
import Skeleton from "react-loading-skeleton";
import {Group} from "../../../services/groups/groupModel";
import {Link} from "react-router-dom";
import classes from "./GroupCard.module.scss";

interface GroupCardProps {
  group?: Group
  onClick?: () => void;
  type?: 'normal' | 'selected' | 'creation';
  isSelected?: boolean;
  className?: string;
  to?: string;
}

const GroupCard: FC<GroupCardProps> = ({group, onClick, className, isSelected, type = 'normal', to}: GroupCardProps) => {

  if (!group) {
    if (type === 'creation') {
      return <Skeleton height={88} borderRadius={14} className={className}/>;
    }
    return type === 'selected'
      ? <Skeleton height={148} borderRadius={16} className={className}/>
      : <Skeleton containerClassName={classes['skeleton']} className={classes['tile-skeleton']} borderRadius={16}/>;
  }

  const members = group.users ?? [];
  const guests = group.guests ?? [];
  const memberCount = members.length + guests.length;

  if (type === 'normal' || type === 'creation') {
    const content = (
      <>
        <div className={classes['members']} aria-label="Group members">
          {members.slice(0, 4).map((member, index) => (
            <Avatar
              key={member.id ?? index}
              alt={member.username}
              title={member.username}
              src={avatarSrc(member)}
              sx={{bgcolor: AVATAR_COLORS[index % AVATAR_COLORS.length]}}
            >
              {initials(member.username)}
            </Avatar>
          ))}
          {guests.slice(0, Math.max(0, 4 - members.length)).map((guest, index) => (
            <Avatar
              key={`guest-${guest.id}`}
              alt={`${guest.name} (guest)`}
              title={`${guest.name} (guest)`}
              sx={{bgcolor: AVATAR_COLORS[(members.length + index) % AVATAR_COLORS.length]}}
            >
              {initials(guest.name)}
            </Avatar>
          ))}
          {memberCount > 4 && <span className={classes['members__more']}>+{memberCount - 4}</span>}
        </div>
        <div className={classes['details']}>
          <span className={classes['name']} title={group.name}>{group.name}</span>
          <span className={classes['metadata']}>
            {memberCount} {memberCount === 1 ? 'member' : 'members'}
            {type === 'normal' && Array.isArray(group.receipts) && (
              <> · {group.receipts.length} {group.receipts.length === 1 ? 'receipt' : 'receipts'}</>
            )}
          </span>
        </div>
      </>
    );
    if (type === 'creation') {
      return (
        <button
          type="button"
          className={`${classes['creation']} ${isSelected ? classes['creation--selected'] : ''} ${className || ''}`}
          onClick={onClick}
          aria-label={`Select group ${group.name}`}
          data-tour="group-card"
          aria-pressed={!!isSelected}
        >
          {content}
        </button>
      );
    }
    const tileClass = `${classes['tile']} ${className || ''}`;
    return to ? (
      <Link to={to} className={tileClass} aria-label={`Open group ${group.name}`}>{content}</Link>
    ) : (
      <button type="button" className={tileClass} onClick={onClick} aria-label={`Open group ${group.name}`}>
        {content}
      </button>
    );
  }

  return (
    <Card
      variant="outlined"
      className={className}
      sx={{
        width: '100%',
        borderRadius: 4,
        borderColor: isSelected ? '#1a2035' : 'rgba(0,0,0,0.06)',
        borderWidth: isSelected ? 2 : 1,
      }}
    >
      <CardActionArea
        onClick={onClick}
        sx={{p: 2, height: '100%'}}
        aria-label={`Select group ${group.name}`}
        aria-pressed={!!isSelected}
      >
        <Stack spacing={1.5} alignItems="flex-start">
          <Avatar variant="rounded" sx={{bgcolor: '#1a2035', width: 48, height: 48}}>
            <GroupsRounded/>
          </Avatar>
          <Typography variant="subtitle1" fontWeight={700} noWrap sx={{width: '100%', color: '#1a2035'}}>
            {group.name}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            {memberCount > 0 && (
              <AvatarGroup
                max={4}
                sx={{'& .MuiAvatar-root': {width: 24, height: 24, fontSize: 11, border: '2px solid #fff'}}}
              >
                {members.map((m, i) => (
                  <Avatar key={m.id ?? i} src={avatarSrc(m)} sx={{bgcolor: AVATAR_COLORS[i % AVATAR_COLORS.length]}}>
                    {initials(m.username)}
                  </Avatar>
                ))}
                {guests.map((guest, index) => (
                  <Avatar
                    key={`guest-${guest.id}`}
                    title={`${guest.name} (guest)`}
                    sx={{bgcolor: AVATAR_COLORS[(members.length + index) % AVATAR_COLORS.length]}}
                  >
                    {initials(guest.name)}
                  </Avatar>
                ))}
              </AvatarGroup>
            )}
            <Typography variant="caption" color="text.secondary">
              {memberCount} {memberCount === 1 ? 'member' : 'members'}
            </Typography>
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  );
};

export default GroupCard;
